import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputLabel, OutlinedInput } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LinkIcon from '@mui/icons-material/Link';
import { LinkTargetField } from '../../components/link-target-field';
import { LinkTargetMode, changeLinkTargetMode, getLinkTargetMode, validateLinkTarget } from '../../components/link-target-utils';
import { FormDrawer, FormDrawerGroup, VisibilitySwitch } from '../../../../components/form-drawer';

export interface MenuDrawerValue {
    title?: string;
    description?: string;
    customPageId?: number;
    url?: string;
    isVisible: boolean;
}

interface MenuDrawerProps {
    open: boolean;
    variant: 'menu' | 'item';
    initial?: MenuDrawerValue;
    // Nome do menu ao qual o item pertence (exibido no cabeçalho)
    parentTitle?: string;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: MenuDrawerValue) => void;
}

const emptyValue: MenuDrawerValue = { isVisible: true };

export function MenuDrawer({ open, variant, initial, parentTitle, isSaving, onClose, onSave }: MenuDrawerProps) {
    const [form, setForm] = useState<MenuDrawerValue>(emptyValue);
    const [mode, setMode] = useState<LinkTargetMode>('none');
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const isMenu = variant === 'menu';
    const linkError = validateLinkTarget(mode, form);
    const titleError = showValidationErrors && !form.title?.trim();

    useEffect(() => {
        if (!open) return;
        const value = initial ? { ...initial } : { ...emptyValue };
        const initialMode = getLinkTargetMode(value);
        // Registros ligados a página também trazem a URL derivada do slug; mantém só o destino ativo
        setForm({ ...value, ...changeLinkTargetMode(value, initialMode) });
        setMode(initialMode);
        setShowValidationErrors(false);
    }, [open, initial]);

    function handleSave() {
        if (!form.title?.trim() || linkError) {
            setShowValidationErrors(true);
            return;
        }
        onSave({
            ...form,
            title: form.title.trim(),
            url: form.url?.trim() || undefined,
            description: isMenu ? form.description : undefined,
        });
    }

    const noun = isMenu ? 'menu' : 'item de menu';
    const subtitle = isMenu
        ? (isEdit ? initial?.title : 'Novo menu de navegação do portal')
        : `Menu: ${parentTitle || ''}`;

    return (
        <FormDrawer
            open={open}
            icon={isMenu ? <MenuIcon /> : <LinkIcon />}
            title={isEdit ? `Editar ${noun}` : `Novo ${noun}`}
            subtitle={subtitle}
            saveLabel={isEdit ? 'Salvar alterações' : `Adicionar ${noun}`}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação" description={isMenu ? 'Nome exibido na barra de navegação do portal.' : 'Nome exibido no submenu.'}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel required>Título</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoFocus
                            autoComplete="off"
                            value={form.title || ''}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            error={titleError}
                        />
                        {titleError && <FormHelperText error>Título é obrigatório</FormHelperText>}
                    </Grid>
                    {isMenu && (
                        <Grid item xs={12}>
                            <InputLabel>Descrição</InputLabel>
                            <OutlinedInput
                                fullWidth
                                multiline
                                minRows={2}
                                autoComplete="off"
                                value={form.description || ''}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                            />
                        </Grid>
                    )}
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup
                title="Link"
                description={isMenu
                    ? 'Opcional: menus com itens normalmente não têm link próprio.'
                    : 'Para onde o item leva: uma página do portal ou um endereço externo.'}
            >
                <LinkTargetField
                    mode={mode}
                    value={form}
                    onChange={(nextMode, target) => {
                        setMode(nextMode);
                        setForm({ ...form, ...target });
                    }}
                    error={showValidationErrors ? linkError : undefined}
                />
            </FormDrawerGroup>

            <FormDrawerGroup title="Exibição">
                <VisibilitySwitch
                    checked={form.isVisible}
                    onChange={(isVisible) => setForm({ ...form, isVisible })}
                    description={isMenu ? 'Menus ocultos e seus itens não aparecem na navegação.' : 'Itens ocultos continuam salvos, mas não aparecem no submenu.'}
                />
            </FormDrawerGroup>
        </FormDrawer>
    );
}
